import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { User, ChevronDown, LogOut, Calendar, ArrowRight, ChevronLeft, ChevronRight, Menu, X, UserCircle } from 'lucide-react'
import { supabaseClient } from '../config/supabase'

interface NewsArticle {
  id: string
  title: string
  excerpt: string | null
  content: string
  image_url: string | null
  category: string
  author_name: string | null
  published_at: string | null
  created_at: string
}

const LandingPage: React.FC = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([])
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false)
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | 'security' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  // Fetch latest news
  useEffect(() => {
    fetchLatestNews()
  }, [])

  // Auto-scroll carousel
  useEffect(() => {
    if (newsArticles.length > 1 && !isAutoScrollPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % newsArticles.length)
      }, 5000) // Change slide every 5 seconds

      return () => clearInterval(interval)
    }
  }, [newsArticles.length, isAutoScrollPaused])

  const fetchLatestNews = async () => {
    try {
      const { data, error } = await supabaseClient
        .from('news')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(6)

      if (error) throw error
      setNewsArticles(data || [])
    } catch (err) {
      console.error('Error fetching news:', err)
    }
  }

  const nextSlide = () => {
    if (newsArticles.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % newsArticles.length)
      setIsAutoScrollPaused(true)
      setTimeout(() => setIsAutoScrollPaused(false), 10000) // Resume auto-scroll after 10s
    }
  }

  const prevSlide = () => {
    if (newsArticles.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + newsArticles.length) % newsArticles.length)
      setIsAutoScrollPaused(true)
      setTimeout(() => setIsAutoScrollPaused(false), 10000) // Resume auto-scroll after 10s
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const getContentPreview = (content: string, maxLength: number = 200): string => {
    const text = stripHtmlTags(content)
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/trailblaze_logo.png" 
                alt="Trailblaze Graduate Tracer System" 
                className="h-20"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-gray-900">Benefits</a>
              <a href="#about" className="text-gray-600 hover:text-gray-900">About UCLM</a>
            </nav>

            {/* Desktop - Conditional Right Side */}
            <div className="hidden md:flex">
            {user ? (
              <div className="flex items-center space-x-4">
                {/* Dashboard Button */}
                <button
                  onClick={() => navigate(getDashboardRoute())}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  Dashboard
                </button>

                {/* Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 focus:outline-none"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                      {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                        <img
                          src={user.profile.profile_picture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/default-avatar.svg'
                          }}
                        />
                      ) : (
                        <User className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                      </div>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false)
                          if (user?.role === 'admin') {
                            navigate('/admin/profile')
                          } else if (user?.role === 'alumni') {
                            navigate('/profile')
                          } else if (user?.role === 'employer') {
                            navigate('/employer/profile')
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>View Profile</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Login Button */
              <Link 
                to="/login"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Login
              </Link>
            )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/news"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  News & Updates
                </Link>
                <a
                  href="#features"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Features
                </a>
                <a
                  href="#benefits"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Benefits
                </a>
                <a
                  href="#about"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About UCLM
                </a>
                
                {/* Mobile Auth Section */}
                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            handleLogout()
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-gray-600 hover:text-red-600 p-2"
                        >
                          <LogOut className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('/uc_nobg.png?v=2')", backgroundPosition: "center 70%" }}
        />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-4">
            <span className="text-blue-600 text-sm font-semibold uppercase tracking-wide">
              University of Cebu Lapu-Lapu and Mandaue
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-black mb-6 leading-tight">
            Track Alumni Success.<br />
            Match Careers.<br />
            Evaluate PEO Alignment.
          </h1>
          <p className="text-xl text-gray-800 mb-8 max-w-4xl mx-auto leading-relaxed">
            Trailblaze is UCLM's comprehensive Graduate Tracer System that systematically tracks 
            alumni employability, facilitates intelligent job matching through THEJMA, 
            and provides data-driven insights for Program Educational Outcomes evaluation.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link 
              to="/register"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-all hover:shadow-xl text-lg transform hover:scale-105"
            >
              Get Started
            </Link>
            <a 
              href="#features"
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-8 py-3 rounded-lg transition-all text-lg hover:shadow-xl"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Alumni Tracking & Career Development
            </h2>
            <p className="text-xl text-gray-600">
              Designed specifically for UCLM to track, engage, and empower alumni
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Alumni Portal */}
            <div className="text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Alumni Portal & Job Matching</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Alumni registration with admin approval workflow</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">PEO/PO surveys tied to Program Educational Outcomes</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">THEJMA-powered job matching & applications</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Career timeline & employment history tracking</span>
                </div>
              </div>
            </div>

            {/* Admin Dashboard */}
            <div className="text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Admin Dashboard & Analytics</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Manage departments, programs, and PEO/PO outcomes</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Approve alumni registrations & employer partnerships</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">PEO/PO score tracking & analytics dashboards</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Exportable reports for accreditation & institutional review</span>
                </div>
              </div>
            </div>

            {/* Employer Portal & Engagement */}
            <div className="text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Employer Portal & Engagement</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Company registration & UCLM partnership application</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Post jobs and manage applications from alumni</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">Real-time in-app notifications for key events</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-600">University news & announcements</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Carousel Section */}
      {newsArticles.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-block">
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 text-sm font-medium rounded-full mb-4">
                  Stay Updated
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Latest News & Updates
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
                Discover the latest happenings, achievements, and events at University of Christ the Living Master
              </p>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full transition-all hover:shadow-lg font-medium"
              >
                View All News
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>

            {/* Carousel */}
            <div className="relative">
              {/* Main Carousel Container */}
              <div 
                className="overflow-hidden rounded-2xl shadow-2xl"
                onMouseEnter={() => setIsAutoScrollPaused(true)}
                onMouseLeave={() => setIsAutoScrollPaused(false)}
              >
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {newsArticles.map((article) => (
                    <div
                      key={article.id}
                      className="w-full flex-shrink-0"
                    >
                      <div className="bg-white">
                        {/* Desktop Layout: Side by side */}
                        <div className="hidden md:grid md:grid-cols-2 gap-0">
                          {/* Image */}
                          <div className="relative h-96">
                            {article.image_url ? (
                              <img
                                src={article.image_url}
                                alt={article.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center">
                                <Calendar className="h-24 w-24 text-white opacity-50" />
                              </div>
                            )}
                            {/* Category Badge */}
                            <div className="absolute top-4 left-4">
                              <span className="px-4 py-2 bg-white/20 backdrop-blur-md text-white text-sm font-medium rounded-full shadow-lg border border-white/30">
                                {article.category}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-12 flex flex-col justify-center">
                            {/* Date */}
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                              <Calendar className="h-4 w-4" />
                              <span>{formatDate(article.published_at || article.created_at)}</span>
                            </div>

                            {/* Title */}
                            <h3 className="text-3xl font-serif font-bold text-gray-900 mb-4 leading-tight">
                              {article.title}
                            </h3>

                            {/* Excerpt */}
                            {article.excerpt && (
                              <p className="text-gray-600 text-lg mb-4 line-clamp-3">
                                {article.excerpt}
                              </p>
                            )}

                            {/* Content Preview */}
                            <p className="text-gray-500 text-base mb-6 line-clamp-4 leading-relaxed">
                              {getContentPreview(article.content, 300)}
                            </p>

                            {/* Read More Button */}
                            <Link
                              to={`/news?articleId=${article.id}`}
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium group"
                            >
                              Read Full Article
                              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>

                        {/* Mobile Layout: Stacked */}
                        <div className="md:hidden">
                          {/* Image */}
                          <div className="relative h-64">
                            {article.image_url ? (
                              <img
                                src={article.image_url}
                                alt={article.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center">
                                <Calendar className="h-20 w-20 text-white opacity-50" />
                              </div>
                            )}
                            {/* Category Badge */}
                            <div className="absolute top-4 left-4">
                              <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-medium rounded-full shadow-lg border border-white/30">
                                {article.category}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            {/* Date */}
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(article.published_at || article.created_at)}</span>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-serif font-bold text-gray-900 mb-3 leading-tight">
                              {article.title}
                            </h3>

                            {/* Excerpt */}
                            {article.excerpt && (
                              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                {article.excerpt}
                              </p>
                            )}

                            {/* Content Preview */}
                            <p className="text-gray-500 text-xs mb-4 line-clamp-3 leading-relaxed">
                              {getContentPreview(article.content, 150)}
                            </p>

                            {/* Read More Button */}
                            <Link
                              to={`/news?articleId=${article.id}`}
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm group"
                            >
                              Read Full Article
                              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              {newsArticles.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition z-10 group"
                    aria-label="Previous news"
                  >
                    <ChevronLeft className="h-6 w-6 text-gray-800 group-hover:text-blue-600" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition z-10 group"
                    aria-label="Next news"
                  >
                    <ChevronRight className="h-6 w-6 text-gray-800 group-hover:text-blue-600" />
                  </button>

                  {/* Slide Indicators */}
                  <div className="flex justify-center gap-2 mt-6">
                    {newsArticles.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-2 rounded-full transition-all ${
                          index === currentSlide
                            ? 'w-8 bg-blue-600'
                            : 'w-2 bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to news ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section id="benefits" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Who's Trailblaze For?
            </h2>
            <p className="text-xl text-gray-600">
              Three user roles — one connected UCLM community
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Alumni */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422A12.083 12.083 0 0121 13c0 4.418-3.582 8-8 8s-8-3.582-8-8c0-.217.008-.432.024-.645L12 14z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">UCLM Alumni</h3>
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">Graduates</p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Register and maintain your career profile</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Respond to PEO/PO surveys to contribute to program evaluation</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Discover and apply for jobs matched by THEJMA</li>
                <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">✓</span> Stay connected with UCLM news and updates</li>
              </ul>
            </div>

            {/* Employers */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Employers</h3>
              <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-3">Any Company Hiring UCLM Graduates</p>
              <p className="text-gray-500 text-xs mb-3">Whether you're a formal industry partner or any business looking to hire — Trailblaze is open to all employers.</p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2"><span className="text-yellow-500 mt-0.5">✓</span> Register your company and post job openings</li>
                <li className="flex items-start gap-2"><span className="text-yellow-500 mt-0.5">✓</span> Apply for official UCLM industry partnership (optional)</li>
                <li className="flex items-start gap-2"><span className="text-yellow-500 mt-0.5">✓</span> Browse and invite qualified alumni candidates</li>
                <li className="flex items-start gap-2"><span className="text-yellow-500 mt-0.5">✓</span> Manage applications and track hiring progress</li>
              </ul>
            </div>

            {/* Admins */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">UCLM Administrators</h3>
              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-3">Program Coordinators & Staff</p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span> Approve alumni registrations and employer partnerships</li>
                <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span> Manage departments, programs, and PEO/PO outcomes</li>
                <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span> Deploy surveys and track alumni outcome scores</li>
                <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span> Access analytics and generate reports</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Join the UCLM Alumni Network?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
            Connect with your alma mater, update your career progress, discover job opportunities 
            through THEJMA, and contribute to the continuous improvement of UCLM programs.
          </p>
          <div className="flex gap-4 justify-center">
            <Link 
              to="/register"
              className="inline-flex items-center bg-white hover:bg-gray-100 text-blue-600 font-semibold px-8 py-3 rounded-lg transition-colors text-lg"
            >
              Create Account
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </Link>
            <Link 
              to="/login"
              className="inline-flex items-center bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 rounded-lg transition-colors text-lg border-2 border-white"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* About UCLM Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section heading */}
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">About the Institution</p>
            <h2 className="text-4xl font-bold text-gray-900">University of Cebu</h2>
            <p className="text-xl text-blue-700 font-semibold mt-1">Lapu-Lapu and Mandaue</p>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              One of the leading campuses of the UC system, established in May 1995 along the Mandaue–Lapu-Lapu corridor.
            </p>
          </div>

          {/* Top row: campus photo + intro text */}
          <div className="flex flex-col lg:flex-row items-start gap-10 mb-10">

            {/* Campus photo */}
            <div className="lg:w-[420px] w-full shrink-0">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/uclm-campus.jpg"
                  alt="UCLM Campus"
                  className="w-full h-64 lg:h-80 object-cover"
                  onError={(e) => {
                    const t = e.currentTarget as HTMLImageElement
                    t.style.display = 'none';
                    (t.nextElementSibling as HTMLElement).style.display = 'flex'
                  }}
                />
                {/* Fallback when photo not yet provided */}
                <div className="w-full h-64 lg:h-80 bg-gradient-to-br from-blue-900 to-blue-700 items-center justify-center hidden">
                  <div className="text-center text-white p-8">
                    <img src="/uc-logo.png" alt="UC Logo" className="w-20 h-20 object-contain mx-auto mb-4 brightness-0 invert" />
                    <p className="font-bold text-lg">University of Cebu</p>
                    <p className="text-blue-200 text-sm">Lapu-Lapu and Mandaue</p>
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5">
                  <p className="text-xs font-semibold text-blue-900">Lapu-Lapu City &amp; Mandaue City, Cebu 🇵🇭</p>
                </div>
              </div>
            </div>

            {/* Intro text + quick stats */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-5">
                <img src="/uc-logo.png" alt="UC Logo" className="w-12 h-12 object-contain shrink-0" />
                <div>
                  <p className="text-sm font-bold text-gray-900">"The Affordable Excellence"</p>
                  <p className="text-xs text-gray-500">University of Cebu System</p>
                </div>
              </div>
              <p className="text-gray-600 mb-3 leading-relaxed">
                UCLM is situated on the <span className="font-medium text-gray-800">Mandaue side</span>, just after the bridge connecting Mandaue and Lapu-Lapu City, one of the most strategically accessible locations in Cebu. Established in <span className="font-medium text-gray-800">May 1995</span>, it is part of the University of Cebu system founded by <span className="font-medium text-gray-800">Atty. Augusto W. Go</span>, one of the Philippines' most trusted private educational institutions.
              </p>
              <p className="text-gray-600 mb-7 leading-relaxed">
                The campus offers a diverse range of programs across engineering, education, business, computing, hospitality, and more, producing graduates equipped to thrive locally and globally.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">May 1995</div>
                  <div className="text-xs text-gray-500 mt-1">Campus Established</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">7+</div>
                  <div className="text-xs text-gray-500 mt-1">Departments</div>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                  <div className="text-lg font-bold text-green-700 leading-tight">Mandaue Side</div>
                  <div className="text-xs text-gray-500 mt-1">Marcelo Fernan Bridge</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom row: Founder · Vision · Mission */}
          <div className="grid md:grid-cols-3 gap-6">

            {/* Founder */}
            <div className="bg-gray-900 rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-yellow-400/10 rounded-full pointer-events-none" />
              <div className="absolute bottom-0 -left-4 w-24 h-24 bg-white/5 rounded-full pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="/augusto.png"
                    alt="Atty. Augusto W. Go"
                    className="w-16 h-16 rounded-full object-cover object-top border-2 border-yellow-400/40 shrink-0"
                    onError={(e) => {
                      const t = e.currentTarget as HTMLImageElement
                      t.style.display = 'none';
                      (t.nextElementSibling as HTMLElement).style.display = 'flex'
                    }}
                  />
                  <div className="w-16 h-16 rounded-full bg-yellow-400/20 border-2 border-yellow-400/40 items-center justify-center shrink-0 hidden">
                    <svg className="w-7 h-7 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-yellow-400 uppercase tracking-widest">Founder</p>
                    <h3 className="text-lg font-bold leading-tight">Atty. Augusto W. Go</h3>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Attorney and visionary educator who founded the University of Cebu in 1964 with an unwavering mission: to make quality higher education accessible and affordable to every Filipino, regardless of economic status.
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-3 py-1">
                  <span className="text-yellow-400 text-xs font-semibold">"The Affordable Excellence"</span>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="bg-blue-900 rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/5 rounded-full pointer-events-none" />
              <div className="absolute bottom-0 -left-4 w-20 h-20 bg-yellow-400/10 rounded-full pointer-events-none" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-blue-300 uppercase tracking-widest mb-1">Vision</p>
                <h3 className="text-lg font-bold mb-3">Premiere University</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  A premiere university in the Philippines, recognized for its commitment to providing quality, affordable education and producing competent, morally upright, and globally competitive graduates.
                </p>
              </div>
            </div>

            {/* Mission */}
            <div className="bg-yellow-400 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-black/5 rounded-full pointer-events-none" />
              <div className="absolute bottom-0 -left-4 w-20 h-20 bg-black/5 rounded-full pointer-events-none" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-black/10 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <p className="text-xs font-semibold text-yellow-900 uppercase tracking-widest mb-1">Mission</p>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Quality for All</h3>
                <p className="text-gray-800 text-sm leading-relaxed">
                  To provide affordable, quality higher education to deserving students and produce graduates who are competent, innovative, and contribute meaningfully to national development and global society.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/traiblaze_nobg.png" 
                  alt="Trailblaze Graduate Tracer System" 
                  className="h-16"
                />
              </div>
              <p className="text-gray-400 text-sm">
                UCLM Graduate Tracer System - Tracking alumni success, supporting career growth
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">System</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="#benefits" className="text-gray-400 hover:text-white">Benefits</a></li>
                <li><Link to="/register" className="text-gray-400 hover:text-white">Register</Link></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white">Login</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="text-gray-400 hover:text-white">About UCLM</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setLegalModal('privacy')} className="text-gray-400 hover:text-white">Privacy Policy</button></li>
                <li><button onClick={() => setLegalModal('terms')} className="text-gray-400 hover:text-white">Terms of Service</button></li>
                <li><button onClick={() => setLegalModal('security')} className="text-gray-400 hover:text-white">Data Security</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2026 University of Cebu Lapu-Lapu and Mandaue. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      {legalModal === 'privacy' && (
        <LegalModal title="Privacy Policy" onClose={() => setLegalModal(null)}>
          <p><strong>Last updated: April 2026</strong></p>
          <p>The University of Cebu Lapu-Lapu and Mandaue (UCLM) is committed to protecting the privacy of its alumni, employers, and administrators who use the Trailblaze platform.</p>
          <h3 className="font-semibold text-gray-800">Information We Collect</h3>
          <p>We collect personal information you provide during registration, including your name, email address, contact number, educational background, and employment history. We also collect usage data such as login activity and survey responses.</p>
          <h3 className="font-semibold text-gray-800">How We Use Your Information</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>To manage your alumni or employer account</li>
            <li>To match alumni with relevant job opportunities via THEJMA</li>
            <li>To conduct PEO/PO outcome surveys for program evaluation</li>
            <li>To generate institutional analytics and accreditation reports for UCLM</li>
            <li>To send notifications about your account, applications, and surveys</li>
          </ul>
          <h3 className="font-semibold text-gray-800">Data Sharing</h3>
          <p>Your personal information is not sold to third parties. Employer profiles and job postings are visible to approved alumni. Alumni career data is accessible to authorized UCLM administrators only for institutional purposes.</p>
          <h3 className="font-semibold text-gray-800">Your Rights</h3>
          <p>You may request to review, update, or delete your personal information by contacting the UCLM Alumni Affairs Office. Certain data may be retained for accreditation compliance purposes.</p>
          <h3 className="font-semibold text-gray-800">Contact</h3>
          <p>For privacy concerns, please contact the UCLM Alumni Affairs Office at the Mandaue campus.</p>
        </LegalModal>
      )}

      {legalModal === 'terms' && (
        <LegalModal title="Terms of Service" onClose={() => setLegalModal(null)}>
          <p><strong>Last updated: April 2026</strong></p>
          <p>By registering and using the Trailblaze Alumni Tracking System, you agree to the following terms set by the University of Cebu Lapu-Lapu and Mandaue (UCLM).</p>
          <h3 className="font-semibold text-gray-800">Eligibility</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Alumni:</strong> Must be a graduate of UCLM. Accounts are subject to admin approval and verification.</li>
            <li><strong>Employers:</strong> Must represent a legitimate registered company. Partnership applications are reviewed by UCLM administrators.</li>
            <li><strong>Administrators:</strong> Accounts are created and managed internally by UCLM.</li>
          </ul>
          <h3 className="font-semibold text-gray-800">Acceptable Use</h3>
          <p>You agree not to misrepresent your identity or credentials, submit false employment or educational information, misuse job postings or applications, or attempt to access data beyond your authorized role.</p>
          <h3 className="font-semibold text-gray-800">Account Suspension</h3>
          <p>UCLM reserves the right to suspend or deactivate accounts that violate these terms, provide fraudulent information, or engage in conduct deemed harmful to the platform or community.</p>
          <h3 className="font-semibold text-gray-800">Surveys & Data Contribution</h3>
          <p>Alumni are encouraged to complete PEO/PO surveys honestly. Survey responses are used solely for program evaluation and institutional improvement. Responses are anonymized in aggregate reports.</p>
          <h3 className="font-semibold text-gray-800">Changes to Terms</h3>
          <p>UCLM may update these Terms of Service periodically. Continued use of Trailblaze after changes constitutes acceptance of the updated terms.</p>
        </LegalModal>
      )}

      {legalModal === 'security' && (
        <LegalModal title="Data Security" onClose={() => setLegalModal(null)}>
          <p><strong>Last updated: April 2026</strong></p>
          <p>Trailblaze is built on Supabase, a secure cloud infrastructure, and follows industry-standard practices to protect your data.</p>
          <h3 className="font-semibold text-gray-800">Authentication</h3>
          <p>All accounts are protected with email/password authentication managed by Supabase Auth. Passwords are never stored in plain text. Session tokens are securely managed and expire automatically.</p>
          <h3 className="font-semibold text-gray-800">Access Control</h3>
          <p>Row-Level Security (RLS) policies are enforced at the database level, ensuring users can only access data they are authorized to view. Admin, alumni, and employer roles each have strictly defined data permissions.</p>
          <h3 className="font-semibold text-gray-800">Data Transmission</h3>
          <p>All data transmitted between your browser and our servers is encrypted using HTTPS/TLS. No sensitive data is transmitted in plain text.</p>
          <h3 className="font-semibold text-gray-800">Storage</h3>
          <p>Uploaded files (profile pictures, resumes) are stored in Supabase Storage with access policies that prevent unauthorized retrieval.</p>
          <h3 className="font-semibold text-gray-800">Reporting Issues</h3>
          <p>If you discover a security vulnerability or suspect unauthorized access to your account, please contact the UCLM IT office or Alumni Affairs immediately.</p>
        </LegalModal>
      )}
    </div>
  )
}

function LegalModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4">
          {children}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="bg-blue-900 hover:bg-blue-800 text-white font-semibold px-6 py-2 rounded-lg transition">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default LandingPage